/**
 * Flavor Journey Component
 * Scroll-driven animation with a paleta cart traveling along a slalom path.
 * Cart position is tied directly to scroll progress through the section.
 */
class FlavorJourney extends HTMLElement {
  constructor() {
    super();
    this.scrollTriggers = [];
    this.resizeObserver = null;
    this.pathData = '';
  }

  connectedCallback() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined' || typeof MotionPathPlugin === 'undefined') {
      this.waitForGSAP().then(() => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      const check = () => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined' && typeof MotionPathPlugin !== 'undefined') {
          gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);
          resolve();
        } else {
          requestAnimationFrame(check);
        }
      };
      check();
    });
  }

  init() {
    gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

    this.cart = this.querySelector('[ref="cart"]');
    this.pathSvg = this.querySelector('[ref="pathSvg"]');
    this.pathBg = this.querySelector('[ref="pathBg"]');
    this.pathProgress = this.querySelector('[ref="pathProgress"]');
    this.groupsContainer = this.querySelector('[ref="groups"]');
    this.groups = this.groupsContainer ? Array.from(this.groupsContainer.children) : [];

    if (!this.cart || this.groups.length === 0) {
      console.warn('FlavorJourney: Missing cart or groups');
      return;
    }

    if (window.innerWidth < 900) {
      this.classList.add('is-initialized');
      return;
    }

    this.generateZigZagPath();
    this.setupScrollAnimation();
    this.setupResizeObserver();
    this.classList.add('is-initialized');
  }

  generateZigZagPath() {
    const rect = this.getBoundingClientRect();
    const sectionWidth = rect.width;

    const centerX = sectionWidth / 2;
    const pathHalfWidth = sectionWidth * 0.2;
    const leftX = centerX - pathHalfWidth;
    const rightX = centerX + pathHalfWidth;

    const groupCenters = this.groups.map(group => {
      const groupRect = group.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();
      return (groupRect.top - sectionRect.top) + (groupRect.height / 2);
    });

    const waypoints = [];

    const firstGroupY = groupCenters[0] || 60;
    const startY = Math.max(40, firstGroupY - 120);
    waypoints.push({ x: centerX, y: startY });

    groupCenters.forEach((centerY, index) => {
      const isEven = index % 2 === 0;
      const targetX = isEven ? leftX : rightX;
      const prevY = index === 0 ? startY : groupCenters[index - 1];

      const approachY = centerY - 40;
      if (approachY > prevY + 50) {
        const midY = (prevY + centerY) / 2;
        const midX = index === 0 ? centerX : (isEven ? rightX : leftX);
        waypoints.push({ x: (midX + targetX) / 2, y: midY });
      }

      waypoints.push({ x: targetX, y: centerY });
    });

    this.pathData = this.generateSmoothPath(waypoints);
    this.pathBg.setAttribute('d', this.pathData);
    this.pathProgress.setAttribute('d', this.pathData);
    this.pathLength = this.pathProgress.getTotalLength();
  }

  generateSmoothPath(points) {
    if (points.length < 2) return '';

    let d = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const verticalDist = Math.abs(curr.y - prev.y);
      const controlOffset = verticalDist * 0.7;

      const cp1x = prev.x;
      const cp1y = prev.y + controlOffset;
      const cp2x = curr.x;
      const cp2y = curr.y - controlOffset;

      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
    }

    return d;
  }

  setupScrollAnimation() {
    this.updateCartPosition(0);

    const progressObj = { value: 0 };

    const tween = gsap.to(progressObj, {
      value: 1,
      ease: 'none',
      onUpdate: () => this.updateCartPosition(progressObj.value),
    });

    const trigger = ScrollTrigger.create({
      trigger: this,
      start: 'top 80%',
      end: 'bottom 20%',
      animation: tween,
      scrub: 0.5,
    });

    this.scrollTriggers = [trigger];
  }

  updateCartPosition(progress) {
    const clampedProgress = Math.max(0, Math.min(1, progress));
    const pathPosition = clampedProgress * this.pathLength;
    const point = this.pathProgress.getPointAtLength(pathPosition);

    const delta = 2;
    const ahead = this.pathProgress.getPointAtLength(
      Math.min(pathPosition + delta, this.pathLength)
    );
    const behind = this.pathProgress.getPointAtLength(
      Math.max(pathPosition - delta, 0)
    );
    const dx = ahead.x - behind.x;
    const dy = ahead.y - behind.y;
    const angle = Math.atan2(dx, dy) * (180 / Math.PI);
    const tilt = Math.max(-10, Math.min(10, angle * 0.35));

    gsap.set(this.cart, {
      x: point.x - this.cart.offsetWidth / 2,
      y: point.y - this.cart.offsetHeight / 2,
      rotation: tilt
    });
  }

  setupResizeObserver() {
    let resizeTimeout;

    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth >= 900) {
          this.resetAnimation();
          this.generateZigZagPath();
          this.setupScrollAnimation();
        }
      }, 200);
    });

    this.resizeObserver.observe(this);
  }

  resetAnimation() {
    this.scrollTriggers.forEach(t => t.kill());
    this.scrollTriggers = [];

    if (this.cart) {
      gsap.killTweensOf(this.cart);
    }
  }

  cleanup() {
    this.resetAnimation();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

if (!customElements.get('flavor-journey')) {
  customElements.define('flavor-journey', FlavorJourney);
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('flavor-journey');
    if (section) {
      section.cleanup?.();
      section.init?.();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('flavor-journey');
    if (section) {
      section.cleanup?.();
    }
  });
}
